import styled from "@emotion/styled"
import { Button, Card, Empty, Select, Skeleton, Tag, Typography, message } from "antd"
import { useMemo, useState } from "react"
import {
  useEnsureDepartmentWarehouse,
  useGetDepartmentStore,
  useGetFactoryStore,
  useGetInventoryMovements,
  useListDepartmentMaster,
} from "hooks/useFactoryDepartmentStock"
import { InventoryStockData } from "services/interface"
import MovementTable from "./MovementTable"
import StockTable from "./StockTable"

const buildProductKey = (
  productId?: string | null,
  productSkuId?: string | null,
  skuVariantId?: string | null
) => `${productId ?? ""}|${productSkuId ?? ""}|${skuVariantId ?? ""}`

const MOVEMENT_PAGE_SIZE = 10

const DepartmentDetailTab = () => {
  const [selectedDept, setSelectedDept] = useState<string | undefined>(undefined)
  const [movementPage, setMovementPage] = useState(1)

  const { data: masterData, isLoading: loadingMaster } =
    useListDepartmentMaster()
  const { data: storeData, isLoading: loadingStore } =
    useGetDepartmentStore(selectedDept)
  const { data: factoryStoreData } = useGetFactoryStore()
  const ensureMutation = useEnsureDepartmentWarehouse()

  const departments = masterData?.data ?? []
  const departmentStore = storeData?.data

  const { data: movementData, isLoading: loadingMovements } =
    useGetInventoryMovements(departmentStore?.warehouse?.id, {
      page_number: movementPage,
      row_per_page: MOVEMENT_PAGE_SIZE,
    })

  const productLookup = useMemo(() => {
    const map = new Map<string, InventoryStockData>()
    const warehouses = factoryStoreData?.data.warehouses ?? []
    warehouses.forEach((wh) => {
      wh.stocks.forEach((stock) => {
        const key = buildProductKey(
          stock.product_id,
          stock.product_sku_id,
          stock.sku_variant_id
        )
        if (!map.has(key)) map.set(key, stock)
      })
    })
    return map
  }, [factoryStoreData])

  const enrichedStocks = useMemo<InventoryStockData[]>(() => {
    if (!departmentStore?.stocks) return []
    return departmentStore.stocks.map((stock) => {
      const hasProductName =
        stock.product_name || stock.product?.name
      const hasSkuName =
        stock.product_sku_name || stock.product_sku?.warehouse_sku_name
      if (hasProductName && (hasSkuName || !stock.product_sku_id)) {
        return stock
      }
      const ref = productLookup.get(
        buildProductKey(
          stock.product_id,
          stock.product_sku_id,
          stock.sku_variant_id
        )
      )
      if (!ref) return stock
      return {
        ...stock,
        product_name: stock.product_name || ref.product_name || ref.product?.name,
        product_image:
          stock.product_image || ref.product_image || ref.product?.image,
        product_sku_name:
          stock.product_sku_name ||
          ref.product_sku_name ||
          ref.product_sku?.warehouse_sku_name,
        sku_variant_name:
          stock.sku_variant_name ||
          ref.sku_variant_name ||
          ref.sku_variant?.sku_value,
        product: stock.product ?? ref.product ?? null,
        product_sku: stock.product_sku ?? ref.product_sku ?? null,
        sku_variant: stock.sku_variant ?? ref.sku_variant ?? null,
      }
    })
  }, [departmentStore, productLookup])

  const handleCreateWarehouse = () => {
    if (!selectedDept) return
    ensureMutation.mutate(selectedDept, {
      onSuccess: () => {
        message.success("สร้างคลังแผนกสำเร็จ")
      },
      onError: () => {
        message.error("สร้างคลังแผนกไม่สำเร็จ")
      },
    })
  }

  return (
    <Wrapper>
      <FilterRow>
        <Typography.Text>เลือกแผนก:</Typography.Text>
        <Select
          showSearch
          allowClear
          placeholder="เลือกแผนก"
          loading={loadingMaster}
          style={{ minWidth: 240 }}
          value={selectedDept}
          onChange={(v) => {
            setSelectedDept(v)
            setMovementPage(1)
          }}
          optionFilterProp="label"
          options={departments.map((d) => ({
            value: String(d.id),
            label: d.description,
          }))}
        />
      </FilterRow>

      {!selectedDept && (
        <Empty
          description="กรุณาเลือกแผนกเพื่อดูข้อมูลสต็อก"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {selectedDept && loadingStore && <Skeleton active />}

      {selectedDept && !loadingStore && departmentStore && (
        <>
          <SummaryCard size="small" bordered>
            <SummaryHeader>
              <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {departmentStore.department?.description || "-"}
                </Typography.Title>
                {departmentStore.warehouse ? (
                  <Tag color="blue" style={{ marginTop: 4 }}>
                    {departmentStore.warehouse.name}
                  </Tag>
                ) : (
                  <Tag color="default">ยังไม่ได้สร้างคลังแผนก</Tag>
                )}
              </div>
            </SummaryHeader>

            {!departmentStore.warehouse && (
              <Button
                type="primary"
                onClick={handleCreateWarehouse}
                loading={ensureMutation.isPending}
                style={{ marginTop: 12 }}
              >
                สร้างคลังแผนกนี้
              </Button>
            )}
          </SummaryCard>

          {departmentStore.warehouse && (
            <Card size="small" bordered style={{ marginTop: 12 }}>
              <StockTable data={enrichedStocks} />
            </Card>
          )}

          {departmentStore.warehouse && (
            <Card size="small" bordered style={{ marginTop: 12 }}>
              <Typography.Title level={5} style={{ marginTop: 0, padding: 10 }}>
                ประวัติการเคลื่อนไหวสต็อก
              </Typography.Title>
              <MovementTable
                data={movementData?.data.movements ?? []}
                loading={loadingMovements}
                total={movementData?.data.total_record ?? 0}
                page={movementPage}
                pageSize={MOVEMENT_PAGE_SIZE}
                onPageChange={setMovementPage}
              />
            </Card>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default DepartmentDetailTab

const Wrapper = styled.div`
  padding: 8px 4px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const SummaryCard = styled(Card)`
  margin-bottom: 12px;
  padding: 8px;
`

const SummaryHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
`
